/**
 * Horizontal layout of the purchase flow (ISSUE-3168).
 *
 * The chrome between the viewport edge and the product listing costs a fixed 260px — page
 * container 2x40, column gap 20, step column 40+20, StepWrapper 2x30, filter gap 40 — on top of
 * the 300px summary column and the 300px filter column. Measured on a live embed session, that
 * left the product listing at its 285px min-content with up to 370px of it clipped below ~1145px
 * of window width. The paddings below therefore step down on narrow viewports, and the filter row
 * wraps on content instead of overflowing.
 *
 * Originally scoped to the Propstack embed, where the iframe is inline in the property page's
 * Media tab; the same arithmetic applies to the standalone flow on a small laptop, so it is not
 * gated on the embed context.
 */

/** Page container padding (the `padding` shorthand of the step+summary wrapper). */
export const PAGE_CONTAINER_PADDING = { xs: '0 .8rem', md: '0 1.6rem', lg: '0 4rem' }

/** Width cap of the step+summary row. Below `sm` the row spans the container. */
export const CONTENT_ROW_MAX_WIDTH = { xs: '100%', sm: '1600px' }

/** Left padding of the step column (MUI spacing factor — 1 = 1rem = 10px). */
export const STEP_COLUMN_PADDING_LEFT = { xs: 1.6, md: 2.4, lg: 4 }

/** Right padding of the step column, between the step content and the summary column. */
export const STEP_COLUMN_PADDING_RIGHT = { xs: .8, md: 1.2, lg: 2 }

/** Horizontal padding inside `StepWrapper`, applied to every step of the flow. */
export const STEP_CONTENT_PADDING_X = { xs: '1.2rem', md: '2rem', lg: '3rem' }

/**
 * Embed chrome (ISSUE-3222): the Propstack panel spends ~289px of the window before the iframe
 * even starts, so the embed flow never has the width the desktop chrome above was designed for —
 * at full-screen laptop widths (~1151-1223px of flow) the product column still read too slim. The
 * embed therefore runs the xs chrome at every width; the reclaimed width goes to the product
 * listing, the only growing column. The standalone flow keeps the responsive maps above.
 *
 * The container's right padding is not the xs value: the left edge stacks container + step column
 * + StepWrapper (8 + 16 + 12 = 36px) before any content shows, while the summary column adds no
 * padding after itself — so the right side mirrors the whole left stack to keep the page
 * symmetric instead of leaving the summary card 8px from the iframe edge.
 */
export const EMBED_PAGE_CONTAINER_PADDING = '0 3.6rem 0 .8rem'
export const EMBED_STEP_COLUMN_PADDING_LEFT = STEP_COLUMN_PADDING_LEFT.xs
export const EMBED_STEP_COLUMN_PADDING_RIGHT = STEP_COLUMN_PADDING_RIGHT.xs
export const EMBED_STEP_CONTENT_PADDING_X = STEP_CONTENT_PADDING_X.xs

/** Page-container and step-column chrome for the given surface. */
export const purchaseFlowChrome = (isEmbed: boolean) => isEmbed
  ? {
    pageContainerPadding: EMBED_PAGE_CONTAINER_PADDING,
    stepColumnPaddingLeft: EMBED_STEP_COLUMN_PADDING_LEFT,
    stepColumnPaddingRight: EMBED_STEP_COLUMN_PADDING_RIGHT,
  }
  : {
    pageContainerPadding: PAGE_CONTAINER_PADDING,
    stepColumnPaddingLeft: STEP_COLUMN_PADDING_LEFT,
    stepColumnPaddingRight: STEP_COLUMN_PADDING_RIGHT,
  }

/**
 * Attribute the embed purchase flow sets on its page container. `StepWrapper` renders bare in
 * tests, so instead of reading the embed context its override is scoped under this attribute via
 * {@link EMBED_CHROME_SELECTOR}.
 */
export const EMBED_CHROME_ATTRIBUTE = 'data-pf-embed-chrome'

/**
 * Emotion selector applying an override only to descendants of the embed page container. Unscoped
 * by media queries, so it outranks every breakpoint of a responsive value by specificity.
 */
export const EMBED_CHROME_SELECTOR = `[${EMBED_CHROME_ATTRIBUTE}] &`

/**
 * Width the product listing is guaranteed, taken from the measured min-content of the card grid in a
 * live flow. It is the listing column's flex basis, and with `minWidth: 0` on that column (see
 * {@link PRODUCT_STEP_LISTING_SX}) it is therefore also the width at which the row wraps: one row
 * needs 300 + gap + this, and below that the two columns each take a full row instead of the listing
 * being squeezed under its content and clipped.
 */
export const PRODUCT_LISTING_MIN_WIDTH = 285

/** Width of the ProductStep filter column while it shares a row with the listing. */
export const PRODUCT_FILTER_WIDTH = 300

/**
 * Gap between the ProductStep filter and listing columns. Owned here rather than as a `gap` prop on
 * the row, because {@link PRODUCT_STEP_WRAP_WIDTH} is computed from it — a second copy on the JSX
 * could move the real wrap point while the container query below kept firing at the old width.
 * Deliberately px, not rem: the two column widths are px, so a rem gap would drift the wrap point
 * away from the threshold for anyone running a non-default root font size.
 */
export const PRODUCT_STEP_ROW_GAP = 40

/** Row width below which the filter and the listing no longer fit side by side and wrap. */
export const PRODUCT_STEP_WRAP_WIDTH = PRODUCT_FILTER_WIDTH + PRODUCT_STEP_ROW_GAP + PRODUCT_LISTING_MIN_WIDTH

/**
 * Container name of the ProductStep filter/listing row, so its children can respond to the row's
 * own width. A viewport breakpoint would be wrong here: the row is what the summary column and the
 * page paddings have already been subtracted from.
 */
export const PRODUCT_STEP_ROW_CONTAINER = 'pf-product-step-row'

/**
 * Makes the ProductStep row a query container and lets its two columns wrap onto separate lines.
 *
 * `container-type: inline-size` is not only a query opt-in — it also applies layout and style
 * containment, so this row becomes the containing block for any `position: fixed`/`absolute`
 * descendant and a new stacking context. Nothing in the subtree relies on that today (tooltips
 * portal out), but an absolutely positioned badge or a non-portalled popper added inside a product
 * card will anchor here rather than to the step column.
 */
export const PRODUCT_STEP_ROW_SX = {
  containerType: 'inline-size',
  containerName: PRODUCT_STEP_ROW_CONTAINER,
  flexWrap: 'wrap',
  gap: `${PRODUCT_STEP_ROW_GAP}px`,
} as const

/**
 * The filter column. Fixed rather than growing, so paired with the listing's `1 1` it reproduces
 * the widths the flow has always had wherever the row fits (`listing = row - 40 - 300`) — only the
 * previously overflowing case behaves differently. Once the row is too narrow to hold both columns
 * the filter has a line to itself, and a 300px card on an empty row just looks stranded, so it
 * takes the full width there.
 */
export const PRODUCT_STEP_FILTER_SX = {
  flex: `0 0 ${PRODUCT_FILTER_WIDTH}px`,
  [`@container ${PRODUCT_STEP_ROW_CONTAINER} (width < ${PRODUCT_STEP_WRAP_WIDTH}px)`]: {
    flexBasis: '100%',
  },
}

/**
 * The listing column — takes the row's remaining width, and wraps below its basis.
 *
 * `minWidth: 0` is what makes the wrap deterministic. A flex item defaults to `min-width: auto`, so
 * without it the line would break on the cards' own min-content instead of the declared basis, and
 * the real wrap point would drift per category and per locale away from the width the container
 * query above is keyed to. With it, {@link PRODUCT_LISTING_MIN_WIDTH} is a requirement the layout
 * enforces rather than a measurement it hopes still holds.
 */
export const PRODUCT_STEP_LISTING_SX = {
  flex: `1 1 ${PRODUCT_LISTING_MIN_WIDTH}px`,
  minWidth: 0,
}

/**
 * Whether the browser honours the container query in {@link PRODUCT_STEP_FILTER_SX}. Reported as an
 * analytics dimension: the fallback (a 300px filter card alone on its row) is intentional, but
 * without this it cannot be told apart from a CSS regression or a stale bundle in support.
 */
export const supportsContainerQueries = (): boolean =>
  typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('container-type', 'inline-size')
